import { useEffect, useMemo, useState } from "react";
import bridge from "../../turbo/bridge";
import ClosableTixxtComposer from "./composer/ClosableTixxtComposer";
import { Controller, FormProvider, useForm } from "react-hook-form";
import FormActions from "../shared/form/fields/FormActions";
import TypeSelector from "./composer/TypeSelector";
import QuestionFields from "./composer/QuestionFields";
import StreamTargeting from "./composer/StreamTargeting";
import saveErrorMessage from "./composer/saveErrorMessage";
import {
  Activitystream,
  useCreateActivity,
  useFile,
  useFileToken,
  useUpdateActivity,
} from "../../api/activitystreams";
import AttachmentsSelector from "./composer/AttachmentsSelector";
import LinkPreviewFromContent from "./composer/LinkPreviewFromContent";
import {
  compact,
  filter,
  find,
  first,
  includes,
  isEmpty,
  isEqual,
  map,
  omit,
  some,
  uniqWith,
} from "lodash";
import { Membership } from "../../@types";
import ExternalIdComponent from "../rundschreiben/ExternalIdComponent";
import { navigate, useSearchParams } from "../../helpers/tixxt-router";
import { Link } from "../shared/LinkPreview";
import formatDate from "../../helpers/formatDate";
import { onValidationError } from "../../helpers/formValidations";
import { useUpdateActivityInCache } from "../../hooks/activitystreams/useUpdateActivityInCache";
import queryClient from "../../helpers/queryClient";
import {
  Activity,
  FileAttachment,
  ImageAttachment,
  PeopleAttachment,
  LinkAttachment,
  ObjectWithContent,
  AppointmentObject,
} from "../../@types/activities.ts";
import classNames from "classnames";
import useProcessActivityFiles from "./hooks/useProcessActivityFiles.ts";

type FormData = {
  object: {
    type: string;
    realType: string;
    content: string;
    translations?: Record<string, Record<string, string>>;
    options?: {
      id?: string;
      label: string;
      placeholder?: string;
      percent?: string;
      vote_count?: number;
      translations?: Record<string, Record<string, string>>;
    }[];
    proposals?: {
      date: string | null;
      time: string | null;
      time_end?: string;
      percent?: string;
      vote_count?: number;
    }[];
    option_adding?: boolean;
    anonymous?: boolean;
    multiple_select?: boolean;
    add_improper_schedule_option?: boolean;
  };
  activitystreams?: (Activitystream | { label?: string; value?: string })[];
  tags?: {
    label: string;
    value: string;
  }[];
  images: (File | ImageAttachment)[];
  files: (File | FileAttachment)[];
  mentions: Membership[];
  link: Link;
  external_id?: string;
  title?: string;
};

export default function ActivityComposer({
  activity,
  currentStream,
  composingRundschreiben = false,
  setStoredActivities,
  closeModal,
  initiallyOpen = false,
  activityToShare,
  clearActivityToShare,
}: {
  activity?: Omit<Activity, "object"> & {
    activitystreams?: Activitystream[];
    object: ObjectWithContent | AppointmentObject;
  };
  currentStream?: { slug: string; name: string } | null;
  composingRundschreiben?: boolean;
  setStoredActivities?: (activities: Activity[]) => void;
  closeModal?: () => void;
  initiallyOpen?: boolean;
  activityToShare?: Activity["shared_from"] | null;
  clearActivityToShare?: () => void;
}) {
  const [searchParams] = useSearchParams();
  const fileId = searchParams.get("fileId");
  const processFileAttachments = useProcessActivityFiles();

  const [open, setOpen] = useState(
    composingRundschreiben || !!activity || initiallyOpen,
  );

  const methods = useForm<FormData>({
    defaultValues: {
      object: {
        type: activity?.object.type || "note",
        realType:
          (includes(["survey", "schedule"], activity?.object.type)
            ? "question"
            : activity?.object.type) || "note",
        content:
          (activity &&
            activity.object.type !== "appointment" &&
            (activity.object as ObjectWithContent).original_content) ||
          "",
        translations:
          (activity &&
            activity.object.type !== "appointment" &&
            (activity.object as ObjectWithContent).translations) ||
          {},
        options:
          activity && activity.object.type === "survey"
            ? activity.object.options
            : undefined,
        proposals:
          activity && activity.object.type === "schedule"
            ? activity.object.proposals
            : undefined,
        anonymous:
          activity &&
          (activity.object.type === "survey" ||
            activity.object.type === "schedule")
            ? activity.object.anonymous
            : undefined,
        option_adding:
          activity &&
          (activity.object.type === "survey" ||
            activity.object.type === "schedule")
            ? activity.object.option_adding
            : undefined,
        multiple_select:
          activity &&
          (activity.object.type === "survey" ||
            activity.object.type === "schedule")
            ? activity.object.multiple_select
            : undefined,
        add_improper_schedule_option:
          activity && "add_improper_schedule_option" in activity.object
            ? activity.object.add_improper_schedule_option
            : undefined,
      },
      activitystreams:
        map(activity?.activitystreams, (stream) => ({
          label: stream.name,
          value: stream.slug,
        })) || [],
      files: activity
        ? (filter(activity.object.attachments, {
            type: "file",
          }) as FileAttachment[])
        : [],
      images: activity
        ? map(
            filter(activity.object.attachments, {
              type: "image",
            }) as ImageAttachment[],
            (image) => ({ ...image, name: image.original_filename }),
          )
        : [],
      mentions: activity
        ? map(
            filter(activity.object.attachments, {
              type: "person",
            }) as PeopleAttachment[],
            (mention) => ({ ...mention, type: "member" }),
          )
        : [],
      tags: activity
        ? map(activity.object.tags, (tag) => ({
            label: tag.name,
            value: tag.id,
          }))
        : [],
      link: activity
        ? first(
            map(
              filter(activity.object.attachments, {
                type: "link",
              }) as LinkAttachment[],
              (link) => ({
                ...link,
                url: link.href,
                description: link.text,
                image_url: link.image,
              }),
            ) as Link[],
          )
        : undefined,
    },
  });

  const { watch, setValue, handleSubmit, reset, control, unregister } = methods;

  const files = watch("files");
  const images = watch("images");
  const realType = watch("object.realType");
  const type = watch("object.type");
  const content = watch("object.content");

  const { prependActivity, updateActivity: updateActivityQueryCache } =
    useUpdateActivityInCache();
  const [sharedFileToken, setSharedFileToken] = useState<string | null>(null);

  // Bumped on successful create to force LinkPreviewFromContent to remount
  // (fresh scan + crawl state) when the app doesn't unmount the composer.
  const [composerInstance, setComposerInstance] = useState(0);

  const initialLinkAttachment = useMemo<Link | null>(() => {
    if (!activity || activity.object.type === "appointment") return null;
    const link = (
      filter(activity.object.attachments, {
        type: "link",
      }) as LinkAttachment[]
    )[0];
    if (!link) return null;
    return {
      id: link.id,
      url: link.href,
      title: link.title,
      description: link.text,
      image_url: link.image,
    };
  }, [activity]);

  const { mutate: getSharedFile } = useFile({
    onSuccess: (file) => {
      setOpen(true);
      setValue("object.type", "note");
      setValue("object.realType", "note");
      setValue("files", [
        {
          id: fileId,
          token: sharedFileToken,
          original_filename: file.original_filename,
          name: file.name,
          url: file.url,
          extension: file.extension,
          external_id: file.external_id,
          type: file.type,
          state: "published",
          content_type: file.content_type,
          created_at: file.created_at,
          size: file.size,
        },
      ]);

      const streams = map(file.activitystream_folder_ids, (slug) => {
        const stream = find(Preload.current_network.activitystreams, { slug });
        return (
          stream && {
            label: stream?.name,
            value: slug,
          }
        );
      });

      const groupStreams = map(file.group_slugs, (slug) => {
        const group = find(Preload.my_groups, { slug });
        return (
          group && {
            label: group?.name,
            value: slug,
          }
        );
      });

      setValue("activitystreams", compact([...streams, ...groupStreams]));
    },
  });

  const { mutate: getFileToken } = useFileToken({
    onSuccess: ({ token }) => setSharedFileToken(token),
  });

  useEffect(() => {
    if (fileId) {
      getFileToken(fileId);
    }
  }, [fileId, getFileToken]);

  useEffect(() => {
    sharedFileToken && fileId && getSharedFile(fileId);
  }, [sharedFileToken, fileId, getSharedFile]);

  function saveToLocalStorage(activityData: any) {
    const existingActivities = JSON.parse(
      localStorage.getItem("activities") || "[]",
    );

    const exists = existingActivities.some((item) =>
      isEqual(omit(item, "id"), omit(activityData, "id")),
    );

    if (exists) return;

    const activityWithTimestamp = {
      ...activityData,
      id: `local_${Date.now()}`,
    };

    const newActivities = uniqWith(
      [...existingActivities, activityWithTimestamp],
      (a, b) => isEqual(omit(a, "id"), omit(b, "id")),
    );

    setStoredActivities?.(newActivities);
  }

  const { mutate: createActivity } = useCreateActivity({
    onSuccess: (activity: Activity) => {
      reset();
      setComposerInstance((n) => n + 1);
      if (window.isApp) {
        bridge.goBack();
      } else {
        setOpen(false);
      }

      activityToShare && clearActivityToShare?.();

      if (composingRundschreiben) {
        navigate(`/activities/${activity.id}`, { replace: true });
      } else {
        prependActivity(activity);
      }
    },
    onError: (_error, variables) => {
      saveToLocalStorage(variables);
      toastr.error(I18n.t("js.generic_error"));
    },
  });

  const { mutate: updateActivity } = useUpdateActivity({
    onSuccess: (data: Activity) => {
      updateActivityQueryCache(
        data.id,
        (activity) =>
          ({
            ...activity,
            activitystream_slugs: data.activitystream_slugs,
            foreign_activitystreams: data.foreign_activitystreams,
            object: {
              ...activity.object,
              ...data.object,
            },
          }) as Activity,
      );
      queryClient.setQueryData(`/activities/${data.id}`, data);
      closeModal && closeModal();
      toastr.success(I18n.t("js.activity.save_success"));
    },
    onError: (error) => {
      closeModal && closeModal();
      toastr.error(saveErrorMessage(error));
    },
  });

  function setRealType(type: string) {
    setValue("object.realType", type);
    setValue("object.type", type);
    !open && setOpen(true);
  }

  useEffect(() => {
    if (activityToShare && !open) setOpen(true);
  }, [activityToShare, open]);

  useEffect(() => {
    if (type === "survey") {
      if (isEmpty(watch("object.options")) || !activity)
        setValue("object.options", [
          {
            label: "",
            placeholder: "js.composer.editor.survey.placeholder.answer_a",
            percent: "0.00",
            vote_count: 0,
          },
          {
            label: "",
            placeholder: "js.composer.editor.survey.placeholder.answer_b",
            percent: "0.00",
            vote_count: 0,
          },
        ]);
    } else {
      if (!isEmpty(watch("object.options"))) {
        unregister("object.options");
      }
      setValue("object.options", undefined);
    }

    if (type === "schedule") {
      if (isEmpty(watch("object.proposals")) || !activity)
        setValue("object.proposals", [
          {
            date: null,
            time: formatDate(new Date(), "HH:mm"),
            percent: "0.00",
            vote_count: 0,
          },
          {
            date: null,
            time: formatDate(new Date(), "HH:mm"),
            percent: "0.00",
            vote_count: 0,
          },
        ]);
    } else {
      if (!isEmpty(watch("object.proposals"))) {
        unregister("object.proposals");
      }
      setValue("object.proposals", undefined);
    }
  }, [type, setValue, unregister, watch, activity]);

  async function submit(data: FormData) {
    const activitystreamSlugs = map(data.activitystreams, "value");

    const attachments: Array<
      | File
      | Membership
      | {
          [key: string]: string | undefined;
        }
    > = [];

    const mentions = data.mentions
      ? map(data.mentions, (mention) => ({ ...mention, type: "person" }))
      : [];

    attachments.push(...mentions);

    if (!isEmpty(data.files)) {
      attachments.push(...(await processFileAttachments(data.files, "files")));
    }

    if (!isEmpty(data.images)) {
      attachments.push(
        ...(await processFileAttachments(data.images, "images")),
      );
    }

    if (!isEmpty(data.link)) {
      attachments.push({
        id: data.link.id,
        href: data.link.url,
        image: data.link.images?.[0] || data.link.image_url,
        text: data.link.description,
        title: data.link.title,
        type: "link",
      });
    }

    const sharedFrom = activityToShare
      ? { shared_from_id: activityToShare?.id }
      : {};

    const externalId = composingRundschreiben
      ? { external_id: `rundschreiben/${data.external_id}` }
      : {};

    const title =
      composingRundschreiben && data.title ? { title: data.title } : {};

    // Shared activities may be posted without own content (#255); the server
    // validation in Activity::ActivityNote makes the same exception.
    if (
      isEmpty(attachments) &&
      isEmpty(data.object.content) &&
      !activityToShare &&
      !activity?.shared_from &&
      (!activity || (activity && activity.object.type !== "appointment"))
    ) {
      toastr.error(I18n.t("js.activity.validation.add_content_error"));
      return;
    }

    const proposals = data.object.proposals
      ? {
          proposals: map(data.object.proposals, (proposal) => ({
            ...proposal,
            date:
              proposal.date && formatDate(new Date(proposal.date), "dd.MM.y"),
          })),
        }
      : {};

    const tags = map(data.tags, (tag) => ({ id: tag.value, name: tag.label }));

    const newActivity = {
      activitystream_slugs: activitystreamSlugs,
      actor: Preload.current_member,
      object: {
        ...data.object,
        ...proposals,
        attachments,
        tags,
      },
      ...sharedFrom,
      ...externalId,
      ...title,
    };

    activity
      ? updateActivity({
          id: activity.id,
          body:
            activity.object.type === "appointment"
              ? { activitystream_slugs: activitystreamSlugs, object: { tags } }
              : newActivity,
        })
      : createActivity(newActivity);
  }

  function isContentRequired() {
    switch (type) {
      case "question":
        return I18n.t("js.activity.validation.add_text_to_question_error");
      case "survey":
        return I18n.t("js.activity.validation.add_question_to_survey_error");
      case "schedule":
        return I18n.t("js.activity.validation.add_content_to_schedule_error");
      default:
        return false;
    }
  }

  return (
    <div
      id="activity-composer"
      className={classNames("activity-composer mb-6", {
        "border-box p-3": !activity && !window.isApp,
      })}
    >
      <FormProvider {...methods}>
        <form
          name="activity"
          onSubmit={handleSubmit(submit, onValidationError)}
        >
          <div className="flex flex-col gap-4">
            {!activity && (
              <div className="author-section flex gap-6 items-center">
                <img
                  className="rounded-full w-12 h-12"
                  alt={Preload.current_member?.name}
                  src={Preload.current_member?.images["40x40@2"]}
                />

                <TypeSelector selectedType={realType} setType={setRealType} />
              </div>
            )}
            {((activity && activity.object.type !== "appointment") ||
              !activity) && (
              <ClosableTixxtComposer
                key={realType}
                name="object.content"
                onOpen={() => setOpen(true)}
                open={open}
                required={isContentRequired()}
                placeholder={I18n.t(
                  `js.composer.editor.${realType}.placeholder`,
                )}
                translatable
                translationsPath="object.translations"
                translationField="content"
              />
            )}
            {open && (
              <>
                {realType === "question" && !activity && (
                  <QuestionFields selectedType={type} />
                )}
                <AttachmentsSelector
                  activityToShare={activityToShare}
                  activityType={activity?.object.type}
                />
                {activity?.object.type !== "appointment" && (
                  <LinkPreviewFromContent
                    key={activity?.id ?? `new-${composerInstance}`}
                    content={content ?? ""}
                    initialLink={initialLinkAttachment}
                  />
                )}
                {(activity?.can?.update_activitystreams || !activity) && (
                  <StreamTargeting
                    currentStream={currentStream}
                    // Sharing (an activity or a ?fileId file) pre-determines
                    // the targeting — the current-stream default must not
                    // interfere. For files it would overwrite the folder/
                    // group streams getSharedFile just set.
                    hasPresetTargeting={!!activityToShare || !!fileId}
                  />
                )}
                {composingRundschreiben && (
                  <Controller
                    name="external_id"
                    control={control}
                    render={({ field }) => (
                      <ExternalIdComponent
                        externalId={field.value}
                        onChangeExternalId={field.onChange}
                        title={watch("title")}
                        onChangeTitle={(value: string) =>
                          setValue("title", value)
                        }
                      />
                    )}
                  />
                )}
                <FormActions
                  onCancel={(e) => {
                    e.preventDefault();
                    reset();
                    setTimeout(() => {
                      // Clearing the share and closing must land in the same
                      // commit: clearing earlier flips hasPresetTargeting
                      // while the just-reset form is still mounted; closing
                      // earlier would let the activityToShare auto-open
                      // effect reopen the composer.
                      activityToShare && clearActivityToShare?.();
                      closeModal ? closeModal() : setOpen(false);
                    }, 100);
                  }}
                  saveText={I18n.t(
                    activity && activity.object.type === "appointment"
                      ? "js.save"
                      : `js.composer.editor.${activity ? "save" : "submit"}`,
                  )}
                  saveDisabled={
                    some(files, {
                      state: "uploading",
                    }) || some(images, { state: "uploading" })
                  }
                />
              </>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
